import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ApiError, apiFetch } from '@/lib/api/rawFetch'
import type { StocktakeDetail, StocktakeScanResponse } from '@/hooks/useStocktakes'

type Scan = { barcode: string; qty?: number }

// Same best-effort feedback as the receiving screen: one lazily-built Audio
// element, and every failure path swallowed so a missing autoplay permission
// can never interrupt counting.
let errorAudio: HTMLAudioElement | null = null

function playScanError() {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return
  if (!errorAudio) errorAudio = new Audio('/scan-error.mp3')
  try {
    errorAudio.currentTime = 0
    const result = errorAudio.play()
    if (result && typeof result.catch === 'function') {
      void result.catch(() => {})
    }
  } catch {
    /* ignore — audio is feedback, not load-bearing */
  }
}

// A shelf is counted by scanning the same item several times in a row, so each
// hit updates one running toast ("+3 Breeches · Black / 28") instead of stacking
// a fresh "+1" per scan. The tally resets once the operator pauses.
const TALLY_WINDOW_MS = 2000

function labelFor(line: { product_title: string; variant_title: string } | null): string {
  if (!line) return ''
  return [line.product_title, line.variant_title].filter(Boolean).join(' · ')
}

export function useStocktakeScan(sessionId: string) {
  const qc = useQueryClient()
  const tally = React.useRef({ barcode: '', qty: 0, at: 0 })

  return useMutation({
    mutationFn: (scan: Scan) =>
      apiFetch<StocktakeScanResponse>('POST', `/api/stocktakes/${sessionId}/scan`, {
        body: { barcode: scan.barcode, qty: scan.qty ?? 1 },
      }),
    onMutate: async (scan) => {
      await qc.cancelQueries({ queryKey: ['stocktake', sessionId] })
      const prev = qc.getQueryData<StocktakeDetail>(['stocktake', sessionId])
      qc.setQueryData<StocktakeDetail>(['stocktake', sessionId], (old) => {
        if (!old) return old
        const qty = scan.qty ?? 1
        const now = new Date().toISOString()
        const lines = old.lines.map((l) => {
          if (!scan.barcode || l.barcode !== scan.barcode) return l
          // A never-counted line (null) starts from 0 on its first scan — the
          // count never starts from the baseline being counted against.
          const counted = (l.counted_qty ?? 0) + qty
          return {
            ...l,
            counted_qty: counted,
            variance: counted - (l.baseline_on_hand ?? 0),
            // Stamped optimistically so the Scan tab, which is ordered by it,
            // puts this row on top the instant the scanner fires.
            last_counted_at: now,
          }
        })
        return { ...old, lines }
      })
      return { prev }
    },
    onSuccess: (result, scan, ctx) => {
      if (!result.matched) {
        // Nothing was written server-side — drop the optimistic increment.
        if (ctx?.prev) qc.setQueryData<StocktakeDetail>(['stocktake', sessionId], ctx.prev)
        playScanError()
        if (result.reason === 'ambiguous') {
          toast.error(`Barcode "${scan.barcode}" is ambiguous in this stocktake`)
        } else {
          toast.error(`Barcode "${scan.barcode}" not found`)
        }
        return
      }

      const qty = scan.qty ?? 1
      const now = Date.now()
      const t = tally.current
      const running =
        (t.barcode === scan.barcode && now - t.at < TALLY_WINDOW_MS ? t.qty : 0) + qty
      tally.current = { barcode: scan.barcode, qty: running, at: now }
      toast.success(`+${running} ${labelFor(result.line) || scan.barcode}`, {
        id: `stocktake-scan-${scan.barcode}`,
        duration: 1400,
      })

      // Somebody else has this variant in an open count at this location. Both
      // sessions apply an ABSOLUTE count to the same stock, so the second apply
      // silently overwrites the first — worth saying out loud, but not worth
      // blocking a scan that already counted.
      const also = result.also_counted_in ?? []
      if (also.length > 0) {
        const more = also.length > 1 ? ` +${also.length - 1} more` : ''
        toast.warning(
          `Also counted in Stocktake #${also[0].session_id} (${also[0].name})${more}`
        )
      }
    },
    onError: (err, _scan, ctx) => {
      if (ctx?.prev) qc.setQueryData<StocktakeDetail>(['stocktake', sessionId], ctx.prev)
      playScanError()
      // 409 = the session left 'counting' somewhere else (another tab, another
      // user finishing the count). Say so rather than showing a bare failure,
      // and pull the real status in so the screen switches out of scan mode.
      if (err instanceof ApiError && err.status === 409) {
        toast.error('This count is no longer open for scanning — reloading it')
        void qc.invalidateQueries({ queryKey: ['stocktake', sessionId] })
        return
      }
      toast.error(err instanceof Error ? err.message : 'Scan failed')
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['stocktake', sessionId] })
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
      // Misses are logged server-side too, so the Log tab is stale after any
      // scan — matched or not.
      qc.invalidateQueries({ queryKey: ['stocktake-scans', sessionId] })
    },
  })
}
